import { useCallback, useEffect, useMemo, useState } from 'react';

import { useEnv } from '@/src/application/hooks/util/useEnv';
import configData from '@/src/config.json';
import { APIService } from '@/src/infrastructure/Protocol/APIService';

import { IRulesService } from './IRulesService';
import { RulesService } from './RulesService';
import { StubRulesService } from './StubRulesService';

export const useRulesService = () => {
  const { env } = useEnv();

  const service = useMemo(() => {
    if (env !== 'storybook') {
      const { URL_EMA_APP, JWT_DEV_TOKEN } = configData.endpoints[env];
      const jwt =
        JWT_DEV_TOKEN !== ''
          ? JWT_DEV_TOKEN
          : (window as Window & typeof globalThis & { __JWTToken: string }).__JWTToken;

      return RulesService.create(new APIService(URL_EMA_APP, jwt));
    } else {
      return StubRulesService.create();
    }
  }, [env]);

  return service;
};

export const useRulesGetterService = <T extends keyof IRulesService>(
  method: T,
  ...p: Parameters<IRulesService[T]>
): {
  data: Awaited<ReturnType<IRulesService[T]>> | undefined;
  error: boolean;
  loading: boolean;
  reFetchData: () => void;
} => {
  const service = useRulesService();
  const [data, setData] = useState<Awaited<ReturnType<IRulesService[T]>>>();
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);

  const params = JSON.stringify(p);

  const fetchData = useCallback(async () => {
    if (!service) throw new Error(`EM_Error The ${typeof service} does not exist`);

    const fn: unknown = service[method];

    if (typeof fn !== 'function')
      throw new Error(`Method '${method}' does not exist on service '${typeof service}'`);

    try {
      setLoading(true);
      const result = (await fn.call(
        service,
        ...(JSON.parse(params) as Parameters<IRulesService[T]>)
      )) as Awaited<ReturnType<IRulesService[T]>>;

      if (!result) throw new Error('Error Rules Service');

      setData(result);
      setLoading(false);
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [service, method, params]);

  const reFetchData = useCallback(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    void fetchData();
  }, [method, service, params, fetchData]);

  return { data, error, loading, reFetchData };
};
